import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [heroSettings, setHeroSettings] = useState(null);
  const [heroLoading, setHeroLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Carregar configuració hero primer
        const heroResponse = await axios.get(`${API}/settings/hero`);
        setHeroSettings(heroResponse.data);
        setHeroLoading(false);
        
        // Carregar productes destacats
        const productsResponse = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(productsResponse.data.slice(0, 6));
      } catch (error) {
        console.error('Error carregant dades:', error);
        // En cas d'error, usar valors per defecte
        setHeroSettings({
          title: 'Una botiga amb ànima',
          subtitle: "Després de gairebé 50 anys de servei al barri de Pedralbes, ens seguim esforçant per dur novetats i solucions creatives per a les necessitats dels nostres clients.",
          image_url: ''
        });
        setHeroLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section data-testid="hero-section" className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 text-sm tracking-widest uppercase text-muted-foreground mb-6">
                <Sparkles className="h-4 w-4" />
                <span>Pedralbes, Barcelona</span>
              </div>
              {heroLoading ? (
                <>
                  <div className="h-16 md:h-24 bg-muted animate-pulse rounded-lg mb-6"></div>
                  <div className="h-20 bg-muted animate-pulse rounded-lg mb-8"></div>
                </>
              ) : (
                <>
                  <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight leading-tight text-foreground mb-6">
                    {heroSettings?.title || 'Una botiga amb ànima'}
                  </h1>
                  <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8">
                    {heroSettings?.subtitle || ''}
                  </p>
                </>
              )}
              <Link to="/catalog">
                <Button 
                  data-testid="cta-catalog"
                  className="rounded-full px-8 py-6 text-base font-medium transition-all hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                  size="lg"
                >
                  Explora el catàleg
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-muted">
                {heroLoading ? (
                  <div className="w-full aspect-[4/3] bg-muted animate-pulse"></div>
                ) : heroSettings?.image_url ? (
                  <img
                    src={heroSettings.image_url}
                    alt="Estel Regals"
                    className="w-full h-auto"
                  />
                ) : (
                  <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                    <Sparkles className="h-16 w-16 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {!loading && featuredProducts.length > 0 && (
        <section data-testid="featured-products" className="py-24 md:py-32 px-6 md:px-12 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl md:text-5xl font-normal tracking-tight text-foreground mb-4">
                Productes Destacats
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                La nostra selecció especial per tu
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            <div className="text-center mt-16">
              <Link to="/catalog">
                <Button 
                  data-testid="view-all-products"
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base"
                  size="lg"
                >
                  Veure tots els productes
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section data-testid="categories-section" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-normal tracking-tight text-foreground mb-4">
              Les Nostres Categories
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'parament', name: 'Parament de la casa', img: 'https://images.unsplash.com/photo-1758789898279-82f8cc279444?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { id: 'bijuteria', name: 'Bijuteria', img: 'https://images.unsplash.com/photo-1758995116106-113264be5365?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { id: 'regals', name: 'Regals originals', img: 'https://images.unsplash.com/photo-1759563874660-e51f23908127?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { id: 'miscelania', name: 'Miscel·lània', img: 'https://images.unsplash.com/photo-1762553395050-ec394919a6ea?crop=entropy&cs=srgb&fm=jpg&q=85' }
            ].map((category) => (
              <Link 
                key={category.id} 
                to={`/catalog?category=${category.id}`}
                data-testid={`category-${category.id}`}
                className="group relative aspect-square rounded-2xl overflow-hidden transition-transform hover:-translate-y-2 duration-500"
              >
                <img
                  src={category.img}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-xl md:text-2xl text-white">
                    {category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;